/**
 * Shared HTTP primitives for the admin client.
 * All admin requests go through the same backend Worker.
 *
 * PHASE 8A — A8-P1-1: 401 responses automatically trigger exactly one
 * refresh attempt (deduplicated across concurrent 401s) and a single
 * retry of the original request. If the refresh fails, the session is
 * cleared via the supplied `onSessionInvalid` hook and the original
 * error is propagated to the caller. The original request NEVER retries
 * indefinitely.
 */

import { config } from '@/config/appConfig';
import { ApiException } from './errors';
import { tokenStorage } from './tokenStorage';
import { refreshAccessToken } from './refreshClient';

// ---------------------------------------------------------------------------
// Module-level wiring for the refresh interceptor.
//
// The refresh interceptor needs read/write access to the session plus the
// ability to notify the AuthContext when the session must be cleared. The
// AuthProvider registers these functions on mount so that this transport
// layer stays free of React dependencies.
//
// `wireRefreshSession({ getRefreshToken, setSession, onSessionInvalid })`
// is called by AuthProvider on mount. The transport uses these functions
// at request-time, not import-time, so test setups without an AuthContext
// still work as long as the wiring has been done.
// ---------------------------------------------------------------------------

interface RefreshSessionHandle {
  getRefreshToken(): string | null;
  setSession(s: { accessToken: string; expiresAt: string }): void;
  onSessionInvalid(): void;
}

let refreshSessionHandle: RefreshSessionHandle | null = null;
let retryBlocked = false; // global circuit-breaker: stop retry storms.

export function wireRefreshSession(handle: RefreshSessionHandle): void {
  refreshSessionHandle = handle;
}

/** Test-only: clear wiring + circuit-breaker between tests. */
export function __resetHttpForTests(): void {
  refreshSessionHandle = null;
  retryBlocked = false;
}

// ---------------------------------------------------------------------------
// Fetch helper with 401 → refresh → retry
// ---------------------------------------------------------------------------

/** Generate a random UUID v4 (browser-compatible). */
function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface FetchWithAuthOptions {
  /**
   * `true` for the original caller; we MUST NOT recurse infinitely on
   * retried requests. Only one retry is allowed per call site.
   */
  isRetry?: boolean;
}

async function fetchWithAuth(
  input: RequestInfo,
  init: RequestInit = {},
  options: FetchWithAuthOptions = {},
): Promise<Response> {
  const session = tokenStorage.get();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (session?.accessToken) {
    headers['Authorization'] = `Bearer ${session.accessToken}`;
  }
  const requestId = uuid();
  headers['X-Request-ID'] = requestId;

  const url = typeof input === 'string' ? input : input.url;
  const resolvedUrl = url.startsWith('/') ? `${config.apiBase}${url}` : url;

  let response: Response;
  try {
    response = await fetch(resolvedUrl, { ...init, headers });
  } catch (err) {
    throw ApiException.network(err);
  }

  // PHASE 8A — A8-P1-1: 401 → refresh → single retry.
  if (response.status === 401 && !options.isRetry && !retryBlocked) {
    // Don't try to refresh the refresh endpoint itself or login.
    const isAuthRoute =
      typeof input === 'string'
        ? input.includes('/admin/auth/login') || input.includes('/admin/auth/refresh')
        : input.url.includes('/admin/auth/login') || input.url.includes('/admin/auth/refresh');
    if (isAuthRoute) return response;

    const handle = refreshSessionHandle;
    if (!handle) {
      // No wiring (i.e. AuthContext not mounted): surface the original 401
      // so legacy/dev flows still behave as before.
      return response;
    }

    const result = await refreshAccessToken({
      getRefreshToken: handle.getRefreshToken,
      setSession: handle.setSession,
      onSessionInvalid: handle.onSessionInvalid,
    });

    if (!result.ok) {
      // Session is cleared in the refresh hook; surface original 401.
      retryBlocked = true;
      return response;
    }

    // Retry the original request exactly once with the fresh access token.
    return fetchWithAuth(input, init, { isRetry: true });
  }

  return response;
}

// ---------------------------------------------------------------------------
// Typed JSON wrapper
// ---------------------------------------------------------------------------

export async function fetchJson<T = unknown>(input: RequestInfo, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetchWithAuth(input, init);
  } catch (err) {
    throw ApiException.network(err);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (err) {
    if (res.ok) throw ApiException.network(err);
    throw ApiException.fromResponse(res, null);
  }

  if (!res.ok) {
    throw ApiException.fromResponse(res, body);
  }

  // Backend uses a `{ data, meta }` envelope for successful responses.
  if (typeof body === 'object' && body !== null && 'data' in body) {
    const outer = body as { data: unknown };
    return outer.data as T;
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// Convenience wrappers
// ---------------------------------------------------------------------------

export const http = {
  get: <T = unknown>(url: string) => fetchJson<T>(url, { method: 'GET' }),
  post: <T = unknown>(url: string, body?: unknown) =>
    fetchJson<T>(url, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(url: string, body?: unknown) =>
    fetchJson<T>(url, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T = unknown>(url: string) => fetchJson<T>(url, { method: 'DELETE' }),
};
